import { SALESFORCE_PERSON_FRAGMENT } from "@/fragments";
import { gql, type TypedDocumentNode } from "@apollo/client";
import type { DateType } from "react-tailwindcss-datepicker";
import { InteractionStats } from "./types";
import type { BackofficeCRMNestedTaskInput } from "@/hooks/useAttachTask";

export const GET_PERSON_INTERACTIONS_QUERY = gql`
  query person($search: ID!, $first: Int, $after: String) {
    person(pgaId: $search) {
      ...SalesforcePersonFragment
    }
  }
  ${SALESFORCE_PERSON_FRAGMENT}
`;

export const CREATE_INTERACTION_MUTATION = gql`
  mutation CreateInteraction($input: BackofficeCRMCreateInteractionInput!) {
    createInteraction(input: $input) {
      success
      interaction {
        id
        createdAt
        categories
        owner {
          name
          email
        }
        showAiSummary
        messages {
          content
          createdAt
          isPrivate
          fromOwner
          haveAccess
        }
        lastMessage {
          content
          createdAt
          isPrivate
          fromOwner
          haveAccess
        }
        jobPost {
          id
          name
          category {
            name
          }
        }
        lastModifiedDate
        interactionDate
        messagesCount
        mode
        participants {
          firstName
          lastName
          profilePhoto
          pgaId
          currentStatus
          classification
          employments {
            isPrimary
            facility {
              name
            }
          }
          primarySectionAffiliation {
            section {
              name
            }
          }
        }
        summary
        relatedTo
        _permissions
      }
      task {
        id
      }
      message
    }
  }
`;

export const CREATE_GROUP_INTERACTION_MUTATION = gql`
  mutation CreateInteraction($input: BackofficeCRMCreateGroupInteractionInput!) {
    createGroupInteraction(input: $input) {
      interaction {
        id
        mode
        lastModifiedDate
        lastMessage {
          content
          createdAt
          isPrivate
        }
      }
      task {
        id
      }
      success
      message
    }
  }
`;

export type BackofficeCRMCreateInteractionInput = {
  participantId?: string | null;
  interactionDate: string | null | DateType;
  mode?: string | null;
  categories?: string[] | null;
  description: string | null;
  summary: string | null;
  privacyRestricted?: boolean;
  jobPostId?: string | null;
  task?: BackofficeCRMNestedTaskInput | null;
};

export type BackofficeCRMCreateGroupInteractionInput = {
  participantIds?: string[] | null;
  interactionDate: string | null | DateType;
  mode?: string | null;
  categories?: string[] | null;
  description: string | null;
  summary: string | null;
  privacyRestricted?: boolean;
  jobPostId?: string | null;
  task?: BackofficeCRMNestedTaskInput | null;
};

export const DELETE_INTERACTION_MUTATION = gql`
  mutation DeleteInteraction($id: ID!) {
    deleteInteraction(id: $id) {
      success
      message
    }
  }
`;

export const UPDATE_INTERACTION_MUTATION = gql`
  mutation UpdateInteraction($input: BackofficeCRMUpdateInteractionInput!) {
    updateInteraction(input: $input) {
      success
      message
    }
  }
`;

export const SHARE_INTERACTION_MUTATION = gql`
  mutation ShareInteraction($input: BackofficeCRMShareInteractionInput!) {
    shareInteraction(input: $input) {
      success
      message
    }
  }
`;

export type ShareInteractionEmailPreview = {
  subject: string;
  html: string;
};

export interface ShareInteractionEmailPreviewQueryResponse {
  shareInteractionEmailPreview: ShareInteractionEmailPreview;
}

export interface ShareInteractionEmailPreviewQueryVariables {
  input: {
    interaction: {
      id: string;
      description: string;
      owner: string;
      date: string | Date;
      mode: string;
      topic: string;
      participants: string[];
    };
    recipients: Array<{ name: string; email: string }>;
    notes?: string | null;
    pgaId: string;
  };
}

export const SHARE_INTERACTION_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  ShareInteractionEmailPreviewQueryResponse,
  ShareInteractionEmailPreviewQueryVariables
> = gql`
  query ShareInteractionEmailPreview($input: BackofficeCRMShareInteractionInput!) {
    shareInteractionEmailPreview(input: $input) {
      subject
      html
    }
  }
`;

export const CREATE_JOB_POST_INTERACTION_MUTATION = gql`
  mutation CreateJobPostInteraction($input: BackofficeCRMCreateJobPostInteractionInput!) {
    createJobPostInteraction(input: $input) {
      success
      interaction {
        id
        mode
        lastMessage {
          content
          createdAt
          isPrivate
        }
        messagesCount
        participants {
          firstName
          lastName
          pgaId
          profilePhoto
        }
        summary
      }
      message
    }
  }
`;

export const statsData: InteractionStats = {
  totalInteractions: 1001,
  totalPhoneInteractions: 99,
  totalVideoInteractions: 97,
  totalSmsInteractions: 95,
  totalEmailInteractions: 42,
  totalInPersonInteractions: 14
};

export const CATEGORIES_QUERY = gql`
  query GetInteractionActivityTypes {
    interactionActivityTypes
  }
`;

export type CategoriesData = { interactionActivityTypes: string[] };

export const INTERACTION_CATEGORIES_METADATA_QUERY = gql`
  query GetInteractionCategoriesMetadata {
    interactionCategories {
      name
      isDefaultPrivate
    }
  }
`;

export type InteractionCategoryMetadata = {
  name: string;
  isDefaultPrivate: boolean;
};

export type InteractionCategoriesMetadataData = {
  interactionCategories: InteractionCategoryMetadata[];
};

// Interaction modes are sourced from the backend via GraphQL introspection
// against the CRMInteractionModeEnum type. The enum's `description` holds the
// human-facing label (e.g. "Phone Call"), and we use that same string as the
// option `value` because `CRMInteraction.mode` is currently stored as the
// human label on the backend. See pga-backend Interaction::MODES_HASH.
export const INTERACTION_MODES_QUERY = gql`
  query GetInteractionModes {
    __type(name: "CRMInteractionModeEnum") {
      enumValues {
        name
        description
      }
    }
  }
`;

export type InteractionModeOption = { value: string; label: string };

export type InteractionModesData = {
  __type: {
    enumValues: Array<{
      name: string;
      description: string | null;
    }> | null;
  } | null;
};

/**
 * Reorders the provided interaction mode options alphabetically while
 * keeping the default value at the top.
 *
 * @param options - Mode options sourced from the backend introspection query
 * @param defaultMode - The default mode value to keep at the top
 */
export const getOrderedModesData = (
  options: readonly InteractionModeOption[],
  defaultMode?: string | null
): InteractionModeOption[] => {
  const sorted = [...options].sort((a, b) => a.label.localeCompare(b.label));

  if (!defaultMode) return sorted;

  const defaultOption = sorted.find((option) => option.value === defaultMode);
  if (!defaultOption) return sorted;

  return [defaultOption, ...sorted.filter((option) => option.value !== defaultMode)];
};

export const interactionPlaceholder =
  "What was the purpose of the conversation? Was there anything noteworthy that stood out to you? Note any specific details that were discussed, including how the conversation concluded and if there were there any next steps outlined.";

export const SEARCH_PEOPLE_QUERY = gql`
  query SearchPeople($searchTerm: String!) {
    people(input: { search: $searchTerm }) {
      nodes {
        id
        firstName
        lastName
        email
        profilePhoto
        pgaId
        phoneNumbers {
          phoneNumber
          phoneType
          phoneFormatted
        }
        primarySection {
          id
          name
        }
      }
    }
  }
`;
